import { computed } from 'vue'
import { useClientContextStore } from 'src/client/stores'
import { useLoader } from 'src/hooks/useLoader'
import { APIS, LOADERS } from 'src/config/constants'
import type { IClientOnboardingFormVM } from 'src/interfaces/client'
import type { IAddress } from 'src/interfaces/common'
import { mergeFamilyWithStudentDrafts } from '../utils/studentHelpers'
import { fetchCachedStudentInfoChanges } from 'src/client/services/studentInfoChanges.client'
import {
  fetchCachedCenterCustomizations,
  getCachedCenterCustomizations,
} from 'src/client/services/centerCustomizations.client'
import {
  requiresOnboarding,
  requiresPendingAuthorization,
} from './onboardingConditions'

export type MemberContactInfoPayload = {
  id?: string
  firstName?: string
  middleName?: string
  lastName?: string
  personalContactInfo: {
    address?: string
    city?: string
    state?: string
    country?: string
    postalCode?: string
    phoneNumber?: string
    emailAddress?: string
  }
  workContactInfo?: IAddress
}

export const useClientOnboarding = () => {
  const contextStore = useClientContextStore()

  const isOnboarding = computed(() => {
    if (!contextStore.family) return false
    const customizations = getCachedCenterCustomizations({
      orgId: contextStore.orgId,
      centerId: contextStore.centerId,
    })
    return requiresOnboarding({
      family: contextStore.family,
      loggedInMember: contextStore.loggedInMember,
      immunizationRecordPreferences: customizations?.immunizationRecord,
    })
  })

  const isPendingAuthorization = computed(() => {
    if (!contextStore.family) return false
    return requiresPendingAuthorization({
      family: contextStore.family,
      loggedInMember: contextStore.loggedInMember,
    })
  })

  /**
   * Get the primary/authorized sponsor member's name for display in pending authorization message
   * The authorized sponsor is the one who needs to complete agreements
   */
  const primaryMemberName = computed(() => {
    const family = contextStore.family
    if (!family) return 'Primary Member'

    // Find the primary member (the one who signs agreements)
    const primaryMember = family.members.find((m) => m.isPrimaryMember)

    return primaryMember?.firstName || 'Primary Member'
  })

  /**
   * Check if user should be allowed to access the client portal
   * Returns false if they're in pending authorization state
   */
  const canAccessClientPortal = computed(() => {
    return !isPendingAuthorization.value
  })

  const resolveIsOnboarding = async () => {
    if (!contextStore.family) return false

    const [studentInfoChanges, customizations] = await Promise.all([
      fetchCachedStudentInfoChanges({
        orgId: contextStore.orgId,
        centerId: contextStore.centerId,
        familyId: contextStore.family.id,
      }),
      fetchCachedCenterCustomizations({
        orgId: contextStore.orgId,
        centerId: contextStore.centerId,
      }),
    ])
    const family = mergeFamilyWithStudentDrafts(
      contextStore.family,
      studentInfoChanges,
    )

    return requiresOnboarding({
      family,
      loggedInMember: contextStore.loggedInMember,
      immunizationRecordPreferences: customizations?.immunizationRecord,
    })
  }

  /**
   * Submit onboarding data to the API
   * This accepts a partial payload - you can submit just the data you have
   * @param payload - Onboarding submission payload
   * @returns Promise with result and error
   */
  const submitOnboardingData = async (payload: {
    nonGuardianEmergencyContact?: IClientOnboardingFormVM
    nonGuardianEmergencyContacts?: IClientOnboardingFormVM[]
    memberContactInfo?: MemberContactInfoPayload
    immunizationInfo?: any
    memberId: string
  }) => {
    const familyId = contextStore.family?.id

    if (!familyId) {
      throw new Error('Family ID is required to submit onboarding data')
    }

    const {
      usePostLoader: postClientOnboarding,
      result,
      error,
    } = useLoader<IClientOnboardingFormVM>(LOADERS.per, {
      uri: `${APIS.clientOnboarding}/${familyId}`,
    })

    await postClientOnboarding(payload)

    return { result, error }
  }

  return {
    isOnboarding,
    isPendingAuthorization,
    primaryMemberName,
    canAccessClientPortal,
    resolveIsOnboarding,
    submitOnboardingData,
  }
}
